import React, { useState } from "react";
import ReactPhoneInput, { CountryData } from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ar from "react-phone-input-2/lang/ar.json";
import TranslatedTextTemp from "../display/TranslatedTextTemp";
import { CheckCircle2, Info } from "lucide-react";
import { translate } from "@modules/localizations/translate";
import TranslatedText from "../display/TranslatedText";

const phoneUtil =
  require("google-libphonenumber").PhoneNumberUtil.getInstance();

interface PhoneInputProps {
  name?: string;
  value?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  setDialCode?: React.Dispatch<React.SetStateAction<string>>;
  setIsValidPhoneNumber?: React.Dispatch<React.SetStateAction<boolean>>;
  onEnterKeyPress?: () => void;
  isError?: boolean;
  useCase?: "onboarding" | "setting" | string;
}

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  function PhoneInputComponent(
    {
      name,
      value,
      disabled = false,
      onChange,
      setDialCode,
      setIsValidPhoneNumber,
      onEnterKeyPress,
      isError,
      useCase,
    },
    ref
  ) {
    const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
    const [isValidNumber, setIsValidNumber] = useState<boolean>(true);
    const [selectedCountry, setSelectedCountry] = useState<{
      countryCode: string;
      dialCode: string;
      format: string;
    }>({ countryCode: "jo", dialCode: "962", format: "XXXXXXXXXX" });

    const errorBorder = phoneNumber?.length
      ? isValidNumber
        ? "1px solid #34CC5A"
        : "1px solid #FD555B"
      : "";

    // **************************** Validation ****************************
    function validatePhoneNumber(phoneNumber: string, regionCode: string) {
      try {
        if (phoneNumber.length > 2) {
          setPhoneNumber(phoneNumber);
          const parsedPhoneNumber = phoneUtil.parse(phoneNumber, regionCode);
          const isValid = phoneUtil.isValidNumber(parsedPhoneNumber);
          setIsValidNumber(isValid);
          if (onChange && isValid) {
            onChange(phoneNumber);
          }
        }
      } catch (error) {
        console.error("Error google validation", error);
      }
    }

    return (
      <div className="relative w-full" dir="ltr">
        <ReactPhoneInput
          inputClass={`!bg-[#D9D9D9] dark:!bg-white dark:text-black !w-full h-[40px] !rounded-lg text-black ${
            phoneNumber &&
            (phoneNumber?.length && isValidNumber
              ? "!border-2 !border-[#34CC5A]"
              : "!border-2 !border-[#FD555B]")
          } ${
            useCase === "setting" && disabled ? "!bg-[#D9D9D9]" : "!bg-white"
          }`}
          buttonClass={`!bg-primary dark:!bg-dark-primary !w-[47px] !rounded-l-lg ${
            phoneNumber &&
            (phoneNumber?.length && isValidNumber
              ? "!border-y-2 !border-y-[#34CC5A] !border-l-2 !border-l-[#34CC5A]"
              : "!border-y-2 !border-y-[#FD555B] !border-l-2 !border-l-[#FD555B]")
          }`}
          searchClass="rtl:text-right ltr:text-left !py !me-3 dark:!bg-background"
          dropdownClass="rtl:text-right ltr:text-left !text-black"
          searchStyle={{
            fontFamily: "Neo Sans Arabic",
            color: "rgba(84, 84, 84, 0.72)",
            fontSize: "10px",
            borderRadius: "12px",
            border: "2px solid rgba(84, 84, 84, 0.70)",
            width: "100%",
          }}
          dropdownStyle={{
            borderRadius: "10px",
            overflowX: "hidden",
          }}
          disabled={disabled}
          // disableCountryCode={true}
          // countryCodeEditable={false}
          // jumpCursorToEnd={false}
          placeholder={`${selectedCountry.format}`}
          inputProps={{
            name: "phone",
            required: true,
            autoFocus: true,
            // minLength: 10,
            // maxLength: 18,
          }}
          country={selectedCountry.countryCode}
          excludeCountries={["il"]}
          localization={ar}
          enableSearch={true}
          disableSearchIcon={true}
          searchPlaceholder={translate(
            `onboarding.inputs.phone.${"search for country"}`
          )}
          searchNotFound={translate(
            `onboarding.inputs.phone.There are no results for your search`
          )}
          onChange={(phone, country: CountryData) => {
            const format = country.format.split(" ");
            format.shift();
            setSelectedCountry({
              countryCode: country.countryCode,
              dialCode: country.dialCode,
              format: format.join(" ").replaceAll(".", "0"),
            });
            const countryCode = country?.countryCode.toUpperCase();
            validatePhoneNumber(phone, countryCode);
          }}
          onEnterKeyPress={onEnterKeyPress}
        />

        {phoneNumber && isValidNumber && phoneNumber?.length > 0 && (
          <CheckCircle2 className="absolute right-2 top-[15%] text-[#34CC5A]" />
        )}
        {phoneNumber && !isValidNumber && phoneNumber?.length > 0 && (
          <Info className="absolute right-2 top-[12%] text-[#FD555B]" />
        )}
        {(!isValidNumber || isError) && (
          <div className="mt-1 text-[12px] font-medium text-[#FD555B] ltr:ml-1 ltr:text-left rtl:mr-1 rtl:text-right">
            <TranslatedText
              text={`onboarding.inputs.errors.${"the entered phone number is incomplete"}`}
            />
          </div>
        )}
      </div>
    );
  }
);
